import type { GlowApiError, Student } from '@innovamat/glow-api-client';
import { useRecoverStudentMutation } from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';

import { recoverStudentInCache } from '../cache-actions/move-student';
import { useOrganization } from './use-organization';

type RecoverStudent = {
  student: Student;
  classroomId?: string | undefined;
};

type Props = {
  onSuccess: () => void;
  onError: (error: GlowApiError) => void;
};

type UseRecoverStudent = {
  onRecoverStudent: ({ student, classroomId }: RecoverStudent) => void;
  isPending: boolean;
  isError: boolean;
};

function useRecoverStudent({ onSuccess, onError }: Props): UseRecoverStudent {
  const queryClient = useQueryClient();
  const { mutate, isPending, isError } =
    useRecoverStudentMutation<GlowApiError>();

  const { organization } = useOrganization();

  const onRecoverStudent = ({ student, classroomId }: RecoverStudent): void => {
    mutate(
      {
        body: {
          studentId: student.id!,
          classroomId,
        },
      },
      {
        onError(error) {
          onError(error);
        },
        onSuccess() {
          const finalClassroomId = classroomId || student.classroomId;

          if (finalClassroomId && organization?.id) {
            recoverStudentInCache({
              queryClient,
              organizationId: organization?.id!,
              classroomId: finalClassroomId,
            });
          }

          onSuccess();
        },
      }
    );
  };

  return {
    onRecoverStudent,
    isPending,
    isError,
  };
}

export { useRecoverStudent };
